import { Button } from 'components/button';
import Card from 'components/card';
import { useCallback, useEffect, useState } from 'react';
import { videoTokenService } from 'views/services/videoTokenService';
import { IToken, TutorialStatusEnum } from 'views/types/token';
import { TokenItem } from './components/TokenItem';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { toast } from 'App';

const TutorialView = () => {
  const [tokensList, setTokensList] = useState<IToken[]>([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [reserved, setReserved] = useState<IToken[]>([]);

  const [tokenAddress, setTokenAddress] = useState('');
  const [setting, setSetting] = useState(false);

  const normalizedTokenAddress = tokenAddress.trim();

  const isButtonDisabled = !normalizedTokenAddress;

  const active = tokensList.find(
    (token) => token.tutorialStatus === TutorialStatusEnum.ACTIVE
  );

  const ended = tokensList.filter(
    (token) => token.tutorialStatus === TutorialStatusEnum.ENDED
  );

  const getTokens = useCallback(async () => {
    const tokens = await videoTokenService.getTutorialTokens();
    setTokensList(tokens);
    const reserve = tokens
      .filter((token) => token.tutorialStatus === TutorialStatusEnum.RESERVE)
      .sort((token) => token.reserveOrder);
    console.log(reserve);
    setOriginalItems(reserve);
    setReserved(reserve);
  }, []);

  useEffect(() => {
    getTokens();
  }, [getTokens]);

  const handleSetToken = async () => {
    try {
      setSetting(true);

      await videoTokenService.setTutorial({
        tokenAddress,
        tutorialStatus: !active
          ? TutorialStatusEnum.ACTIVE
          : TutorialStatusEnum.RESERVE,
        reserveOrder: !active ? null : reserved.length + 1,
      });
      setTokenAddress('');
      getTokens();
    } catch (e) {
      console.log(e);
    } finally {
      setSetting(false);
    }
  };

  const handleChangeOrder = async ({
    contractAddress,
    newOrder,
  }: {
    contractAddress: string;
    newOrder: number;
  }) => {
    try {
      await videoTokenService.setTutorial(
        {
          tokenAddress: contractAddress,
          reserveOrder: newOrder,
          tutorialStatus: TutorialStatusEnum.RESERVE,
        },
        false
      );
    } catch (e) {
      console.log(e);
    }
  };

  const handleSetTokenAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTokenAddress(e.target.value);
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const syncChangesWithBackend = useCallback(
    async (newItems: IToken[]) => {
      const originalOrderMap = new Map(
        originalItems.map((item) => [item.id, item.reserveOrder])
      );

      const changesToSend: { contractAddress: string; newOrder: number }[] = [];
      newItems.forEach((item, newIndex) => {
        const originalOrder = originalOrderMap.get(item.id);

        if (originalOrder !== newIndex + 1) {
          changesToSend.push({
            contractAddress: item.contractAddress,
            newOrder: newIndex + 1,
          });
        }
      });

      if (changesToSend.length === 0) {
        return;
      }

      try {
        await Promise.all(
          changesToSend.map((change) => {
            handleChangeOrder(change);
            return new Promise((resolve) => setTimeout(resolve, 500));
          })
        );

        setOriginalItems(
          newItems.map((item, index) => ({ ...item, reserveOrder: index + 1 }))
        );
        toast({
          title: 'Success',
          description: 'Order successfully changed',
          status: 'success',
          position: 'top-right',
        });
      } catch (error) {
        setTokensList(originalItems);
        toast({
          title: 'Error',
          description: 'Failed change video order',
          status: 'error',
          position: 'top-right',
        });
      }
    },
    [originalItems]
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = reserved.findIndex((item) => item.id === active.id);
      const newIndex = reserved.findIndex((item) => item.id === over.id);

      const reorderedItems = arrayMove(reserved, oldIndex, newIndex);
      setReserved(reorderedItems);

      syncChangesWithBackend(reorderedItems);
    }
  }

  return (
    <Card
      extra={
        'w-full min-h-[400px] h-[calc(100vh-150px)] flex-1 sm:overflow-auto px-6 pb-4 mt-8'
      }
    >
      <div className="pt-10 flex flex-col items-start">
        <p className="font-medium text-lg">Set new tutorial token</p>
        <div className="flex gap-2 items-center pt-4 sm:flex-row sm:gap-5 w-full sm:pt-5">
          <input
            type="text"
            placeholder="Enter contract address"
            className="border rounded-md block min-h-7 bg-lightPrimary px-4 py-1 text-base font-medium text-navy-700 outline-none placeholder:!text-gray-400 sm:py-2 md:w-[500px] w-80 sm:h-full invalid:border-red-500"
            value={tokenAddress}
            onChange={handleSetTokenAddress}
          />
          <Button
            disabled={isButtonDisabled}
            onClick={handleSetToken}
            className="border p-2 min-w-28 rounded-md border-gray-800 hover:text-black flex cursor-pointer items-center gap-2 text-gray-600 hover:font-medium"
          >
            {setting ? 'Adding...' : 'Add tutorial'}
          </Button>
        </div>
      </div>
      <div className="pt-10 flex flex-col items-start gap-3">
        <p className="font-medium text-lg">Tutorial tokens list:</p>
        <div className="flex flex-col w-full gap-4">
          <div className="flex flex-col w-full gap-2">
            <p className="font-medium">Active video:</p>
            {active ? (
              <TokenItem token={active} i={0} />
            ) : (
              <p> No active airdrop video provided</p>
            )}
          </div>
          {reserved?.length && (
            <div className="flex flex-col w-full gap-2">
              <p className="font-medium">Reserved video:</p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={reserved}
                  strategy={verticalListSortingStrategy}
                >
                  {reserved.map((token, i) => {
                    return (
                      <TokenItem
                        token={token}
                        i={i}
                        key={token.contractAddress}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </div>
          )}
          {ended?.length && (
            <div className="flex flex-col w-full gap-2">
              <p className="font-medium">Previous videos:</p>
              {ended.map((token, i) => {
                return (
                  <TokenItem token={token} i={i} key={token.contractAddress} />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TutorialView;
